import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import {
  COLORS, FRAMING, FRAME_COLORS, MTO_SIZES, mtoPrice,
  checkWord, artistMatch, eur,
} from "@/lib/shop";

// Live preview of the personalised print: brush X + handwritten word.
function Preview({ word, color }) {
  const xColor = color === "bicolor" ? "#c2402a" : "#111111";
  return (
    <div
      className="relative border gallery-line bg-white aspect-[4/5] w-full overflow-hidden"
      data-testid="mto-preview"
    >
      <svg viewBox="0 0 400 500" className="absolute inset-0 h-full w-full">
        <g stroke={xColor} strokeWidth="34" strokeLinecap="round" fill="none" opacity="0.94">
          <path d="M118 118 C 150 190, 240 260, 285 355" />
          <path d="M285 115 C 240 200, 160 265, 118 352" />
        </g>
      </svg>
      <p
        className="absolute bottom-[16%] right-[10%] max-w-[70%] text-right text-[#111111]"
        style={{
          fontFamily: "'Caveat', cursive",
          fontStyle: "italic",
          fontSize: "clamp(20px, 3.2vw, 34px)",
          lineHeight: 1.25,
        }}
        data-testid="mto-preview-word"
      >
        The {word || "______"} Goes Here.
      </p>
    </div>
  );
}

export default function MadeToOrder() {
  const { addItem } = useCart();
  const { t, lang } = useLanguage();
  const [word, setWord] = useState("");
  const [color, setColor] = useState("noir");
  const [size, setSize] = useState("50x70");
  const [framing, setFraming] = useState("unframed");
  const [frameColor, setFrameColor] = useState("oak");
  const [ack, setAck] = useState(false);
  const [added, setAdded] = useState(false);

  const check = useMemo(() => checkWord(word), [word]);
  const match = useMemo(
    () => (check.reason === "protected" ? artistMatch(word) : null),
    [check, word]
  );
  const price = mtoPrice(size, framing);
  const ready = word.trim().length > 0 && check.allowed && ack;

  const onAdd = () => {
    if (!ack) {
      toast.error(t("mto.legalRequired"));
      return;
    }
    if (!ready) return;
    addItem({
      product_id: "made-to-order",
      product_name: `The ${word.trim()} Goes Here`,
      custom_word: word.trim(),
      color,
      size,
      framing,
      frame_color: framing === "framed" ? frameColor : null,
      quantity: 1,
      unit_price: price,
      image: null,
    });
    setAdded(true);
    toast.success(t("product.added"));
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-20 md:pt-24 pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
            {t("mto.kicker")}
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#111111]">
            {t("mto.title.a")}
            <span className="italic">{word.trim() || t("mto.title.blank")}</span>
            {t("mto.title.b")}
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-[#111111]/70">
            {t("mto.intro")}
          </p>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <Reveal className="lg:col-span-7">
              <Preview word={word.trim()} color={color} />
              <p className="mt-4 text-xs font-light text-[#111111]/45">
                {t("mto.unique")} — {t("mto.review")}
              </p>
            </Reveal>

            <div className="lg:col-span-5">
              {/* Word */}
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">
                  {t("mto.wordLabel")}
                </label>
                <input
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  maxLength={24}
                  placeholder={t("mto.wordPlaceholder")}
                  data-testid="mto-word"
                  className="mt-3 w-full border border-[#111111]/25 bg-white px-4 py-3.5 font-light text-lg outline-none focus:border-[#111111]"
                  style={{ fontFamily: "'Caveat', cursive" }}
                />
                {word.trim() && !check.allowed && (
                  <div className="mt-3 border border-[#c2402a]/40 bg-[#c2402a]/5 px-4 py-3" data-testid="mto-blocked">
                    <p className="text-sm font-light text-[#8c2e1d]">
                      {check.reason === "protected" && (match ? t("mto.blocked.protected") : t("mto.blocked.protectedBrand"))}
                      {check.reason === "profanity" && t("mto.blocked.profanity")}
                      {check.reason === "invalid" && (lang === "fr" ? "Entre 1 et 24 caractères." : "Between 1 and 24 characters.")}
                    </p>
                    {match && (
                      <Link
                        to={`/shop/${match.id}`}
                        data-testid="mto-catalogue-link"
                        className="link-underline mt-2 inline-block text-sm font-light text-[#111111]"
                      >
                        {match.name} →
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* Colourway */}
              <div className="mt-7">
                <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">{t("product.color")}</p>
                <div className="mt-3 flex gap-3">
                  {COLORS.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`mto-color-${c.id}`}
                      onClick={() => setColor(c.id)}
                      className={`rounded-full border px-5 py-2 text-sm font-light transition-colors duration-300 ${
                        color === c.id
                          ? "border-[#111111] bg-[#111111] text-[#faf8f5]"
                          : "border-[#111111]/25 text-[#111111]/80 hover:border-[#111111]"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div className="mt-7">
                <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">{t("product.size")}</p>
                <div className="mt-3 flex flex-col gap-3">
                  {MTO_SIZES.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`mto-size-${s.id}`}
                      onClick={() => setSize(s.id)}
                      className={`flex items-center justify-between border px-5 py-3 text-left transition-colors duration-300 ${
                        size === s.id ? "border-[#111111] bg-[#f1ede7]" : "border-[#111111]/20 hover:border-[#111111]/50"
                      }`}
                    >
                      <span className="font-light text-[#111111]">{s.label}</span>
                      <span className="text-sm text-[#111111]/70">{eur(mtoPrice(s.id, framing), lang)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Framing */}
              <div className="mt-7">
                <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">{t("product.framing")}</p>
                <div className="mt-3 flex gap-3">
                  {FRAMING.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`mto-framing-${f.id}`}
                      onClick={() => setFraming(f.id)}
                      className={`flex-1 border px-5 py-3 text-sm font-light transition-colors duration-300 ${
                        framing === f.id ? "border-[#111111] bg-[#f1ede7]" : "border-[#111111]/20 hover:border-[#111111]/50"
                      }`}
                    >
                      {t(`product.${f.id}`)}
                    </button>
                  ))}
                </div>
              </div>

              {framing === "framed" && (
                <div className="mt-7">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">{t("product.frameColor")}</p>
                  <div className="mt-3 flex gap-3">
                    {FRAME_COLORS.map((f) => (
                      <button
                        key={f.id}
                        data-testid={`mto-frame-${f.id}`}
                        onClick={() => setFrameColor(f.id)}
                        className={`flex-1 border px-5 py-3 text-sm font-light transition-colors duration-300 ${
                          frameColor === f.id ? "border-[#111111] bg-[#f1ede7]" : "border-[#111111]/20 hover:border-[#111111]/50"
                        }`}
                      >
                        {t(f.id === "oak" ? "product.oak" : "product.blackOak")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <p className="mt-8 text-3xl font-light text-[#111111]" data-testid="mto-price">
                {eur(price, lang)}
              </p>

              {/* Personalised-goods withdrawal exemption — explicit acknowledgement */}
              <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="mto-legal">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  data-testid="mto-legal-check"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#111111]"
                />
                <span className="text-xs font-light leading-relaxed text-[#111111]/65">
                  {t("mto.legal")}
                </span>
              </label>

              <button
                onClick={onAdd}
                disabled={!ready}
                data-testid="mto-add"
                className="mt-6 flex w-full items-center justify-center gap-2 bg-[#111111] px-7 py-4 text-sm font-light tracking-wide text-[#faf8f5] transition-opacity duration-500 hover:opacity-85 disabled:opacity-40"
              >
                {added ? (
                  <><Check className="h-4 w-4" strokeWidth={1.5} /> {t("product.added")}</>
                ) : (
                  `${t("mto.add")} — ${eur(price, lang)}`
                )}
              </button>
              <p className="mt-4 text-xs font-light text-[#111111]/45">{t("fulfil.line")}</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
